import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { useFirebase } from '#features/api/utils/firebase';
import { getMeOptions } from '#modules/user/model/me';
import { useIsAuth } from '#modules/user/features/auth/hooks/useIsAuth';
import { envConfig } from '#constants/envConfig';
import { localStorage } from '#utils/localStorage';
import { addPushToken } from '../model/notifications';

/**
 * Reconciles this browser's FCM token with the backend once per load.
 *
 * The web flow only ever sent the token on an explicit "enable" click, so any drift stayed
 * forever: FCM rotates a token on its own (SW reinstall, cleared site data, server-side
 * refresh), and — more often — the token belongs to the browser rather than to the account, so
 * signing in as somebody else leaves the row attached to the previous user. Both cases end with
 * a live subscription the backend doesn't know about: no notifications, and a "this browser is
 * not registered" banner that clicking the button could not fix.
 *
 * Runs only when permission is already granted, so it never triggers a prompt. Mount once, high
 * in the tree; native has its own listener-based sync (useNativePushTokenSync).
 */
export const useWebPushTokenSync = () => {
  const isAuth = useIsAuth();
  const firebaseClient = useFirebase();
  const queryClient = useQueryClient();
  const { data: me } = useQuery({ ...getMeOptions(), enabled: isAuth });
  const serverTokens =
    me?.__typename === 'CurrentUser'
      ? (me.pushToken ?? [])
          .map((entry) => entry?.token)
          .filter((token): token is string => typeof token === 'string' && token.length > 0)
      : null;
  // One reconciliation per token value: without this the effect would re-send on every profile
  // refetch while the backend keeps answering with a list that doesn't contain our token.
  const syncedRef = useRef<string | null>(null);

  useEffect(() => {
    if (envConfig.IS_NATIVE || !isAuth || !firebaseClient || serverTokens === null) {
      return;
    }

    if (typeof window === 'undefined' || !('Notification' in window)) {
      return;
    }

    if (Notification.permission !== 'granted') {
      return;
    }

    let active = true;

    void (async () => {
      let token: string | null = null;
      try {
        token = await firebaseClient.getPushToken();
      } catch (error) {
        console.warn('[push] token sync: getPushToken threw', error);
      }

      if (!active || !token || syncedRef.current === token) {
        return;
      }

      if (serverTokens.includes(token)) {
        // Already known to the backend — keep the local copy in step with it.
        localStorage.setItem('fcm_token', token);
        syncedRef.current = token;
        return;
      }

      try {
        const data = await addPushToken({ token, platform: 'WEB' });
        if (data?.__typename === 'Operation') {
          localStorage.setItem('fcm_token', token);
          syncedRef.current = token;
          queryClient.invalidateQueries({ queryKey: getMeOptions().queryKey });
        }
      } catch (error) {
        console.warn('[push] token sync: addPushToken failed', error);
      }
    })();

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuth, firebaseClient, queryClient, serverTokens?.join('|')]);
};
